import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Printer, Download, QrCode } from 'lucide-react';

const FRONTEND_URL = window.location.origin;

const QRCodeGenerator = ({ open, onClose, type, id, name, yardName, yardLocation }) => {
  const qrRef = useRef(null);
  
  // Generate the URL that the QR code will link to
  const getQRUrl = () => {
    if (type === 'yard') {
      return `${FRONTEND_URL}/yard/${id}`;
    } else if (type === 'hive') {
      return `${FRONTEND_URL}/hive/${id}`;
    } else if (type === 'swarm-trap') {
      return `${FRONTEND_URL}/swarm-traps?id=${id}`;
    }
    return FRONTEND_URL;
  };

  const getTypeLabel = () => {
    if (type === 'yard') return `🏡 Yard: ${name}`;
    if (type === 'hive') return `🐝 ${name}`;
    if (type === 'swarm-trap') return `🪤 Trap: ${name}`;
    return name;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const qrElement = qrRef.current;
    
    if (printWindow && qrElement) {
      const svgData = qrElement.innerHTML;
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Code - ${name}</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
              }
              .container {
                text-align: center;
                padding: 40px;
                border: 2px dashed #ccc;
                border-radius: 16px;
              }
              h1 {
                margin: 0 0 8px 0;
                font-size: 24px;
                color: #333;
              }
              .type {
                color: #666;
                font-size: 14px;
                margin-bottom: 20px;
                text-transform: uppercase;
              }
              .qr-code {
                margin: 20px 0;
              }
              .url {
                font-size: 10px;
                color: #999;
                word-break: break-all;
                max-width: 250px;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; }
                .container { border: 2px solid #000; }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <h1>${name}</h1>
              <div class="type">${getTypeLabel()} — QR Label</div>
              ${yardName ? `<div style="color:#b45309;font-size:14px;margin-bottom:4px;">${yardName}</div>` : ''}
              ${yardLocation ? `<div style="color:#999;font-size:12px;margin-bottom:12px;">${yardLocation}</div>` : ''}
              <div class="qr-code">${svgData}</div>
              <div class="url">${getQRUrl()}</div>
            </div>
            <script>
              window.onload = function() { window.print(); window.close(); }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleDownload = () => {
    const qrElement = qrRef.current?.querySelector('svg');
    if (qrElement) {
      const svgData = new XMLSerializer().serializeToString(qrElement);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `qr-${type}-${name.replace(/\s+/g, '-')}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
            <QrCode className="h-6 w-6 text-amber-600" />
            QR Code for {name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center py-6">
          <div className="text-sm text-stone-500 mb-1">
            {getTypeLabel()}
          </div>
          {yardName && (
            <div className="text-sm font-medium text-amber-700 mb-0.5">{yardName}</div>
          )}
          {yardLocation && (
            <div className="text-xs text-stone-400 mb-2">{yardLocation}</div>
          )}
          
          <div 
            ref={qrRef}
            className="bg-white p-4 rounded-xl border-2 border-stone-200 shadow-sm"
          >
            <QRCodeSVG
              value={getQRUrl()}
              size={200}
              level="H"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#000000"
              imageSettings={{
                src: "/thrive-logo.png",
                x: undefined,
                y: undefined,
                height: 28,
                width: 52,
                excavate: true,
              }}
            />
          </div>
          
          <p className="text-xs text-stone-400 mt-3 text-center max-w-xs">
            Scan this QR code to quickly access this {type}
          </p>
          
          <div className="text-xs text-stone-300 mt-2 text-center break-all px-4">
            {getQRUrl()}
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleDownload}
            className="flex-1 h-12"
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
          <Button
            onClick={handlePrint}
            className="flex-1 h-12 bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRCodeGenerator;
